/**
 * What Benny will accept on a message, and how big it may be.
 *
 * These rules MIRROR the server's validation on POST /ai/assistant/chat/stream
 * (`apps/api/src/ai/assistant.controller.ts`): 4 files, images matched by
 * `image/*`, a fixed document set, 4.5 MB per document, 20 MB total decoded.
 * Keeping clients in sync with it means a rejection is a friendly message
 * instead of a 400 after the upload has already been paid for.
 *
 * Pure data + predicates — no File/Blob/FileReader, so mobile can use it too.
 */

export type AttachmentKind = 'image' | 'document'

export const MAX_ATTACHMENTS = 4

/** Per-image ceiling. Below the server's total budget, kept as a UX guard. */
export const MAX_IMAGE_BYTES = 5 * 1024 * 1024 // 5 MB

/** Bedrock Converse caps each document block at 4.5 MB — server enforces too. */
export const MAX_DOCUMENT_BYTES = 4.5 * 1024 * 1024

/** Server's decoded ceiling across every attachment on one message. */
export const MAX_TOTAL_ATTACHMENT_BYTES = 20 * 1024 * 1024

/** Content types the server accepts as documents (images match `image/*`). */
export const ACCEPTED_DOCUMENT_CONTENT_TYPES = [
  'application/pdf',
  'text/plain',
  'text/csv',
  'text/markdown',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
] as const

/**
 * Extension → content type. Pickers are unreliable here: `.md` usually
 * arrives as `text/plain` or an empty string, `.csv` sometimes as
 * `application/vnd.ms-excel`. The extension wins when we recognize it, so the
 * server sees a content type from its accepted set.
 */
export const ACCEPTED_DOCUMENT_EXTENSIONS: Record<string, string> = {
  pdf: 'application/pdf',
  txt: 'text/plain',
  csv: 'text/csv',
  md: 'text/markdown',
  markdown: 'text/markdown',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
}

/** Both halves of "what counts as a document", for callers that want one import. */
export const ACCEPTED_DOCUMENT_TYPES = {
  contentTypes: ACCEPTED_DOCUMENT_CONTENT_TYPES,
  extensions: ACCEPTED_DOCUMENT_EXTENSIONS,
} as const

/** File-picker `accept` string covering everything the server takes. */
export const ATTACHMENT_ACCEPT = 'image/*,.pdf,.txt,.csv,.md,.docx,.xlsx'

const DOCUMENT_CONTENT_TYPE_SET: ReadonlySet<string> = new Set(
  ACCEPTED_DOCUMENT_CONTENT_TYPES,
)

export function attachmentExtension(fileName: string): string {
  const dot = fileName.lastIndexOf('.')
  return dot >= 0 ? fileName.slice(dot + 1).toLowerCase() : ''
}

/**
 * Which bucket a content type falls in, or null when the server would reject
 * it outright.
 */
export function attachmentKind(contentType: string): AttachmentKind | null {
  if (contentType.startsWith('image/')) return 'image'
  if (DOCUMENT_CONTENT_TYPE_SET.has(contentType)) return 'document'
  return null
}

/** The per-file byte ceiling for a kind. */
export function maxAttachmentBytes(kind: AttachmentKind): number {
  return kind === 'image' ? MAX_IMAGE_BYTES : MAX_DOCUMENT_BYTES
}

/**
 * Resolve the content type we'll actually send. Falls back to the extension
 * map when the picker's type is missing or outside the accepted sets; null
 * when the file is not something Benny can read.
 */
export function resolveAttachmentContentType(
  fileName: string,
  declaredType?: string | null,
): string | null {
  const byExtension =
    ACCEPTED_DOCUMENT_EXTENSIONS[attachmentExtension(fileName)]
  if (byExtension) return byExtension
  if (declaredType && declaredType.startsWith('image/')) return declaredType
  if (declaredType && DOCUMENT_CONTENT_TYPE_SET.has(declaredType)) {
    return declaredType
  }
  return null
}

/**
 * Would the server take this file? `kind` narrows it further — a composer
 * that only takes images passes 'image' and gets a false for a PDF.
 */
export function isAcceptedAttachment(
  contentType: string,
  size: number,
  kind?: AttachmentKind | null,
): boolean {
  const resolved = attachmentKind(contentType)
  if (!resolved) return false
  if (kind && resolved !== kind) return false
  return size <= maxAttachmentBytes(resolved)
}

/** "4.5 MB" / "20 MB" — for the message shown when a file is too big. */
export function formatAttachmentBytes(bytes: number): string {
  const mb = bytes / 1024 / 1024
  return `${Number.isInteger(mb) ? mb : mb.toFixed(1)} MB`
}
